import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getInterviewHub, getCandidates, getEmployees, proposeInterviewSlots, scheduleInterview, deleteInterview, deleteSlotOffer, quickCreateCandidate, addToJobPipeline, getJobs } from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import TimezoneSelect, { browserTimezone } from "../components/TimezoneSelect";
import { DateInput } from "../components/DateInput";
import { futureTimeSlots } from "../utils/timeSlots";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useIsMobile } from "../hooks/useIsMobile";
import { TabPillBar } from "../components/TabPillBar";
import { Plus, Search, Video, MapPin, Phone, Trash2, ArrowRight, X, RefreshCw } from "lucide-react";

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  awaiting_interviewer: { label: "Awaiting Interviewer", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  awaiting_candidate:  { label: "Awaiting Candidate",   color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  confirmed:           { label: "Confirmed",             color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  scheduled:           { label: "Scheduled",             color: "#4338ca", bg: "#eef2ff", border: "#c7d2fe" },
  expired:             { label: "Expired",               color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
};

// Tab order mirrors the pipeline: waiting on us → waiting on them → agreed →
// booked → dead. `scheduled` previously had no chip at all, so directly-booked
// interviews were only reachable by clearing every filter.
const TAB_ORDER = ["awaiting_interviewer", "awaiting_candidate", "confirmed", "scheduled", "expired"] as const;

const TYPE_LABELS: Record<string, string> = {
  video: "Video",
  in_person: "In-Person",
  phone: "Phone",
};

const TYPE_ICONS: Record<string, typeof Video> = {
  video: Video,
  in_person: MapPin,
  phone: Phone,
};

/** Initials disc — same derivation as CandidatesPage's table avatar. */
function Avatar({ name, size = 34 }: { name?: string | null; size?: number }) {
  const initials = (name || "?").split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "#eff6ff", color: "#1e3a8a",
      display: "grid", placeItems: "center", fontSize: size <= 28 ? 11 : 12, fontWeight: 700,
      flexShrink: 0, letterSpacing: "0.02em",
    }}>{initials}</div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cfg = STATUS_LABELS[status] || { label: status, color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" };
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

function TypeChip({ type, duration }: { type: string; duration?: number | null }) {
  const Icon = TYPE_ICONS[type] || Video;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, background: "#f1f5f9",
      borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#334155", whiteSpace: "nowrap",
    }}>
      <Icon size={12} strokeWidth={2} />
      {TYPE_LABELS[type] || type}{duration ? ` · ${duration}m` : ""}
    </span>
  );
}

function fmtSlot(iso: string | null, tz: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: tz, weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

function ageLabel(createdAt: string | null) {
  if (!createdAt) return null;
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const diffH = diffMs / 3_600_000;
  if (diffH < 24) return { text: "Today", color: "#15803d", bg: "#dcfce7" };
  if (diffH < 48) return { text: "1d ago", color: "#d97706", bg: "#fef9c3" };
  const days = Math.floor(diffH / 24);
  return { text: `${days}d ago`, color: "#dc2626", bg: "#fee2e2" };
}

const BLANK_FORM = {
  interview_type: "video",
  duration_minutes: "60",
  location_or_link: "",
  interviewer_id: "",
  interviewer_name: "",
  interviewer_email: "",
  timezone: browserTimezone(),
  interview_date: new Date().toISOString().split("T")[0],
  interview_time: "10:00",
  additional_attendees: "",
};

export default function InterviewHubPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const isMobile = useIsMobile(768);
  const [tab, setTab] = useState<string>("awaiting_interviewer");
  const [search, setSearch] = useState("");
  const [detailRow, setDetailRow] = useState<any | null>(null);

  // New interview modal
  const [modalStep, setModalStep] = useState<0 | 1 | 2>(0); // 0=closed, 1=pick candidate, 2=fill details
  const [selectedApp, setSelectedApp] = useState<{ applicationId: number; candidateId: number; candidateName: string; jobTitle: string } | null>(null);
  const [scheduleMode, setScheduleMode] = useState<"direct" | "propose">("direct");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", email: "", job_id: "" });
  const [manualCreating, setManualCreating] = useState(false);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Fetch the whole hub once and filter by tab in the browser. The endpoint has
  // no pagination, and the previous per-status fetch made the chip counts lie:
  // `counts` was derived from the already-status-filtered response, so every
  // other status read 0 the moment a filter was on.
  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ["interview-hub"],
    queryFn: () => getInterviewHub(),
  });

  const { data: allCandidates = [] } = useQuery({
    queryKey: ["candidates"],
    queryFn: () => getCandidates(),
    enabled: modalStep === 1,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: getEmployees,
    enabled: modalStep === 2,
  });

  const { data: allJobs = [] } = useQuery({
    queryKey: ["jobs"],
    queryFn: getJobs,
    enabled: modalStep === 1 && manualMode,
  });

  const counts = (rows as any[]).reduce((acc: any, r: any) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  // Filter rows by tab, then search
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return (rows as any[]).filter((r: any) => {
      if (r.status !== tab) return false;
      if (!q) return true;
      return (
        r.candidate_name?.toLowerCase().includes(q) ||
        r.job_title?.toLowerCase().includes(q) ||
        r.interviewer_name?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, tab]);

  // Escape closes the detail sheet. Bound only while it is open so it never
  // competes with the New Interview modal's own dismissal.
  useEffect(() => {
    if (!detailRow) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDetailRow(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailRow]);

  // Candidates with at least one application for step 1
  const candidatesWithApps = useMemo(() => {
    const q = candidateSearch.toLowerCase().trim();
    return (allCandidates as any[]).filter((c: any) =>
      c.application_id != null &&
      (!q || c.name?.toLowerCase().includes(q) || c.job_title?.toLowerCase().includes(q))
    );
  }, [allCandidates, candidateSearch]);

  function openModal() {
    setModalStep(1);
    setSelectedApp(null);
    setCandidateSearch("");
    setManualMode(false);
    setManualForm({ name: "", email: "", job_id: "" });
    setForm({ ...BLANK_FORM });
    setScheduleMode("direct");
  }

  function closeModal() {
    setModalStep(0);
    setSelectedApp(null);
    setManualMode(false);
    setSending(false);
  }

  async function handleManualNext() {
    if (!manualForm.name.trim() || !manualForm.email.trim() || !manualForm.job_id) return;
    setManualCreating(true);
    try {
      const cand = await quickCreateCandidate({ name: manualForm.name.trim(), email: manualForm.email.trim() });
      const job = (allJobs as any[]).find((j: any) => String(j.id) === manualForm.job_id);
      const result = await addToJobPipeline(parseInt(manualForm.job_id), cand.candidate_id);
      setSelectedApp({
        applicationId: result.application_id,
        candidateId: cand.candidate_id,
        candidateName: cand.name,
        jobTitle: job?.title || "Unknown Job",
      });
      setModalStep(2);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast(msg || "Failed to create candidate", "error");
    } finally {
      setManualCreating(false);
    }
  }

  async function handleSend() {
    if (!selectedApp) return;
    setSending(true);
    try {
      if (scheduleMode === "direct") {
        if (!form.interview_date) { toast("Please pick an interview date", "error"); setSending(false); return; }
        await scheduleInterview(selectedApp.candidateId, {
          interview_type: form.interview_type,
          duration_minutes: parseInt(form.duration_minutes),
          location_or_link: form.location_or_link.trim(),
          interviewer_id: form.interviewer_id ? parseInt(form.interviewer_id) : undefined,
          interview_date: form.interview_date,
          interview_time: form.interview_time,
          timezone: form.timezone,
          application_id: selectedApp.applicationId,
          additional_attendees: form.additional_attendees.trim(),
        });
        toast("Interview scheduled — calendar invites sent to candidate, interviewer and you!", "success");
      } else {
        if (!form.interviewer_email || !form.interviewer_name) { toast("Interviewer name and email are required", "error"); setSending(false); return; }
        await proposeInterviewSlots(selectedApp.applicationId, {
          interview_type: form.interview_type,
          duration_minutes: parseInt(form.duration_minutes),
          location_or_link: form.location_or_link.trim(),
          interviewer_id: form.interviewer_id ? parseInt(form.interviewer_id) : undefined,
          interviewer_name: form.interviewer_name,
          interviewer_email: form.interviewer_email,
          timezone: form.timezone,
        });
        toast("Availability request sent to interviewer!", "success");
      }
      qc.invalidateQueries({ queryKey: ["interview-hub"] });
      closeModal();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to schedule interview", "error");
    } finally {
      setSending(false);
    }
  }

  async function handleDelete(r: any) {
    const ok = await confirm("Delete this interview? This cannot be undone.", { confirmLabel: "Delete", danger: true });
    if (!ok) return;
    setDeletingId(r.id);
    try {
      if (r.row_type === "offer") {
        await deleteSlotOffer(r.id);
      } else {
        await deleteInterview(r.interview_id || r.id);
      }
      qc.invalidateQueries({ queryKey: ["interview-hub"] });
      setDetailRow(null);
      toast("Interview deleted", "success");
    } catch { toast("Failed to delete", "error"); }
    finally { setDeletingId(null); }
  }

  /** Confirmed/scheduled rows show the booked time; pending ones show the proposal. */
  function displaySlot(r: any) {
    if (r.status === "confirmed" || r.status === "scheduled") return fmtSlot(r.confirmed_slot, r.timezone || "UTC");
    if (!r.slot_1) return null;
    const extra = [r.slot_2, r.slot_3].filter(Boolean).length;
    return `${fmtSlot(r.slot_1, r.timezone)}${extra ? ` +${extra} more` : ""}`;
  }

  return (
    <div>
      {/* Width and padding come from ShellMain (80px 32px 32px, and the
          mobile override in responsive.css). Capping the page here made it
          narrower than every sibling list page and squeezed the table until
          the actions column scrolled off. Matches CandidatesPage. */}
      <LoadingOverlay active={sending || manualCreating || deletingId !== null} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>Interview Hub</h2>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>All interview scheduling activity across every job and candidate</div>
        </div>
        <button onClick={openModal}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: isMobile ? "11px 18px" : "9px 18px", minHeight: isMobile ? 44 : undefined, background: "#1e3a8a", color: "white", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
          <Plus size={15} strokeWidth={2.5} /> New Interview
        </button>
      </div>

      {/* Tab pills + search */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <TabPillBar
          ariaLabel="Filter interviews by status"
          active={tab}
          onSelect={setTab}
          style={{ marginBottom: 0 }}
          items={TAB_ORDER.map(k => ({ key: k, label: STATUS_LABELS[k].label, count: counts[k] || 0 }))}
        />
        <div style={{ position: "relative", marginLeft: isMobile ? 0 : "auto", width: isMobile ? "100%" : 260 }}>
          <Search size={14} strokeWidth={2} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#64748b", pointerEvents: "none" }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search candidate, job, interviewer"
            style={{ width: "100%", padding: isMobile ? "11px 12px 11px 32px" : "8px 12px 8px 32px", minHeight: isMobile ? 44 : undefined, border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, outline: "none", color: "#334155", boxSizing: "border-box" }} />
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#64748b", fontSize: 14, background: "white", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          {search ? `No matches for "${search}"` : "No interviews here. Create one with New Interview."}
        </div>
      ) : isMobile ? (
        /* ── Mobile: card list. Interview Hub is an approval-style list, so the
           design system's standard below 768px is cards, not a scroll-table. ── */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((r: any) => {
            const age = r.status !== "confirmed" && r.status !== "expired" ? ageLabel(r.created_at) : null;
            const slot = displaySlot(r);
            return (
              <div key={r.id} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", padding: 14 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Avatar name={r.candidate_name} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "6px 8px", alignItems: "flex-start" }}>
                      <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 15, minWidth: 0, overflowWrap: "anywhere" }}>{r.candidate_name}</div>
                      <StatusPill status={r.status} />
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, overflowWrap: "anywhere" }}>{r.candidate_email}</div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "10px 0" }}>
                  <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{r.job_title}</span>
                  <TypeChip type={r.interview_type} duration={r.duration_minutes} />
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: "#334155" }}>
                      with {r.interviewer_name || "—"} ·{" "}
                      {slot
                        ? <span style={{ fontWeight: 600, color: r.status === "confirmed" ? "#16a34a" : "#334155" }}>{slot}</span>
                        : <span style={{ color: "#64748b" }}>Not scheduled</span>}
                    </div>
                    {r.timezone && <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{r.timezone}</div>}
                  </div>
                  {age && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: age.bg, color: age.color, whiteSpace: "nowrap" }}>{age.text}</span>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => setDetailRow(r)}
                    style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, minHeight: 44, borderRadius: 6, border: "1px solid #cbd5e1", background: "white", color: "#334155", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    View Profile <ArrowRight size={14} strokeWidth={2} />
                  </button>
                  <button onClick={() => handleDelete(r)} disabled={deletingId === r.id} aria-label="Delete interview"
                    style={{ minWidth: 44, minHeight: 44, display: "grid", placeItems: "center", borderRadius: 6, border: "1px solid #fecaca", background: "white", color: "#dc2626", cursor: deletingId === r.id ? "not-allowed" : "pointer" }}>
                    <Trash2 size={15} strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="table-wrap" style={{ background: "white", borderRadius: 10, border: "1px solid #e2e8f0", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 780 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {["Candidate", "Job", "Interviewer", "Type / Duration", "Slot", "Requested", "Status", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r: any, i: number) => {
                const age = r.status !== "confirmed" && r.status !== "expired" ? ageLabel(r.created_at) : null;
                const slot = displaySlot(r);
                return (
                  <tr key={r.id} onClick={() => setDetailRow(r)}
                    style={{ borderBottom: i < filtered.length - 1 ? "1px solid #f1f5f9" : "none", cursor: "pointer" }}>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                        <Avatar name={r.candidate_name} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.candidate_name}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{r.candidate_email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "11px 14px", color: "#334155", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.job_title}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                        <Avatar name={r.interviewer_name} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: "#334155" }}>{r.interviewer_name || "—"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{r.interviewer_email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}><TypeChip type={r.interview_type} duration={r.duration_minutes} /></td>
                    <td style={{ padding: "11px 14px", maxWidth: 200 }}>
                      <div style={{ color: r.status === "confirmed" ? "#16a34a" : "#334155", fontWeight: r.status === "confirmed" ? 600 : 400 }}>
                        {slot || <span style={{ color: "#64748b" }}>Not scheduled</span>}
                      </div>
                      {(r.status === "confirmed" || r.status === "scheduled") && r.location_or_link && (
                        <a href={r.location_or_link.startsWith("http") ? r.location_or_link : `https://${r.location_or_link}`}
                          target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          style={{ fontSize: 11, color: "#1e3a8a", textDecoration: "none", fontWeight: 600 }}>
                          Join →
                        </a>
                      )}
                      {r.status !== "confirmed" && r.timezone && (
                        <div style={{ fontSize: 11, color: "#64748b" }}>{r.timezone}</div>
                      )}
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      {age ? (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: age.bg, color: age.color, whiteSpace: "nowrap" }}>{age.text}</span>
                      ) : <span style={{ color: "#64748b", fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 14px" }}><StatusPill status={r.status} /></td>
                    <td style={{ padding: "11px 14px" }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {r.candidate_id && (
                          <button onClick={() => nav(`/candidates?highlight=${r.candidate_id}`)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "white", color: "#334155", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" }}>
                            View Profile <ArrowRight size={13} strokeWidth={2} />
                          </button>
                        )}
                        <button onClick={() => handleDelete(r)} disabled={deletingId === r.id} aria-label="Delete interview"
                          style={{ display: "grid", placeItems: "center", padding: "5px 8px", borderRadius: 6, border: "1px solid #fecaca", background: "white", color: "#dc2626", cursor: deletingId === r.id ? "not-allowed" : "pointer" }}>
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {filtered.length} record{filtered.length !== 1 ? "s" : ""} · {STATUS_LABELS[tab]?.label}
          {search ? ` · matching "${search}"` : ""}
        </span>
        <button onClick={() => refetch()}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", color: "#1e3a8a", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: 4 }}>
          <RefreshCw size={12} strokeWidth={2.5} /> Refresh
        </button>
      </div>
      {/* ── New Interview Modal ── */}
      {modalStep > 0 && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={closeModal}>
          <div style={{ background: "white", borderRadius: 12, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>

            {/* Step 1 — Pick candidate + job */}
            {modalStep === 1 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: 17 }}>New Interview — Step 1 of 2</h3>
                  <button onClick={closeModal} aria-label="Close" style={{ display: "grid", placeItems: "center", minWidth: 32, minHeight: 32, background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><X size={18} strokeWidth={2} /></button>
                </div>

                {/* Toggle: existing vs manual */}
                <div style={{ display: "flex", gap: 0, marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  {[{ key: false, label: "From candidates list" }, { key: true, label: "Enter manually" }].map(opt => (
                    <button key={String(opt.key)} onClick={() => setManualMode(opt.key as boolean)}
                      style={{ flex: 1, padding: "8px", fontSize: 12, fontWeight: manualMode === opt.key ? 700 : 500, background: manualMode === opt.key ? "#1e3a8a" : "white", color: manualMode === opt.key ? "white" : "#64748b", border: "none", cursor: "pointer" }}>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {!manualMode ? (
                  <>
                    <p style={{ margin: "0 0 12px", fontSize: 12, color: "#64748b" }}>Select a candidate already in the system.</p>
                    <input value={candidateSearch} onChange={e => setCandidateSearch(e.target.value)}
                      placeholder="Search candidates…"
                      style={{ width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, boxSizing: "border-box", marginBottom: 12, outline: "none" }} />
                    {candidatesWithApps.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "24px 0", color: "#64748b", fontSize: 13 }}>
                        No candidates found. Switch to "Enter manually" to add a new one.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto" }}>
                        {candidatesWithApps.map((c: any) => (
                          <button key={c.application_id} onClick={() => {
                            setSelectedApp({ applicationId: c.application_id, candidateId: c.candidate_id, candidateName: c.name, jobTitle: c.job_title || "Unknown Job" });
                            setModalStep(2);
                          }}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", textAlign: "left", width: "100%" }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{c.name}</div>
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{c.job_title || "No job"}</div>
                            </div>
                            {c.score != null && (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: c.score >= 70 ? "#dcfce7" : c.score >= 50 ? "#fef9c3" : "#fee2e2", color: c.score >= 70 ? "#15803d" : c.score >= 50 ? "#d97706" : "#dc2626" }}>
                                {c.score}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>Enter the candidate's details and pick a job. A profile will be created automatically.</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Full Name *</label>
                        <input value={manualForm.name} onChange={e => setManualForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="Jane Smith"
                          style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Email *</label>
                        <input type="email" value={manualForm.email} onChange={e => setManualForm(f => ({ ...f, email: e.target.value }))}
                          placeholder="jane@example.com"
                          style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Job *</label>
                        <select value={manualForm.job_id} onChange={e => setManualForm(f => ({ ...f, job_id: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, background: "white" }}>
                          <option value="">Select a job…</option>
                          {(allJobs as any[]).filter((j: any) => j.status !== "closed").map((j: any) => (
                            <option key={j.id} value={j.id}>{j.title}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button onClick={handleManualNext}
                        disabled={!manualForm.name.trim() || !manualForm.email.trim() || !manualForm.job_id || manualCreating}
                        style={{ background: manualForm.name && manualForm.email && manualForm.job_id && !manualCreating ? "#1e3a8a" : "#94a3b8", color: "white", border: "none", padding: "9px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                        {manualCreating ? "Creating…" : "Next →"}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Step 2 — Fill interview details */}
            {modalStep === 2 && selectedApp && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div>
                    <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: 17 }}>New Interview — Step 2 of 2</h3>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                      {selectedApp.candidateName} · <span style={{ color: "#334155" }}>{selectedApp.jobTitle}</span>
                    </div>
                  </div>
                  <button onClick={closeModal} aria-label="Close" style={{ display: "grid", placeItems: "center", minWidth: 32, minHeight: 32, background: "none", border: "none", cursor: "pointer", color: "#64748b", flexShrink: 0 }}><X size={18} strokeWidth={2} /></button>
                </div>
                {/* Schedule mode toggle */}
                <div style={{ display: "flex", gap: 0, marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  {([["direct", "📅 Schedule Now"], ["propose", "🤖 Automatic Scheduling"]] as const).map(([mode, label]) => (
                    <button key={mode} onClick={() => setScheduleMode(mode)}
                      style={{ flex: 1, padding: "9px 8px", fontSize: 12, fontWeight: scheduleMode === mode ? 700 : 500, background: scheduleMode === mode ? "#1e3a8a" : "white", color: scheduleMode === mode ? "white" : "#64748b", border: "none", cursor: "pointer" }}>
                      {label}
                    </button>
                  ))}
                </div>
                <p style={{ margin: "-8px 0 16px", fontSize: 12, color: "#64748b" }}>
                  {scheduleMode === "direct"
                    ? "Pick a date and time — ICS calendar invites go to the candidate, interviewer, and you immediately."
                    : "The interviewer gets an email to pick 3 available slots. The candidate chooses one. A Meet link is auto-generated and ICS invites go to everyone."}
                </p>

                {scheduleMode === "direct" && (() => {
                    const todayStr = new Date().toISOString().split("T")[0];
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Date *</label>
                          <DateInput value={form.interview_date} min={todayStr}
                            onChange={e => setForm(f => ({ ...f, interview_date: e.target.value }))}
                            style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Time *</label>
                          <select value={form.interview_time} onChange={e => setForm(f => ({ ...f, interview_time: e.target.value }))}
                            style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, background: "white" }}>
                            {futureTimeSlots(form.interview_date, todayStr).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })()}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Interview Type</label>
                    <select value={form.interview_type} onChange={e => setForm(f => ({ ...f, interview_type: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      <option value="video">Video</option>
                      <option value="in_person">In Person</option>
                      <option value="phone">Phone</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Duration</label>
                    <select value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }}>
                      {[15, 30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Interview Timezone</label>
                  <TimezoneSelect value={form.timezone} onChange={tz => setForm(f => ({ ...f, timezone: tz }))} style={{ width: "100%", boxSizing: "border-box" }} />
                </div>

                {/* Location field — only shown where it makes sense */}
                {form.interview_type === "in_person" ? (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Location</label>
                    <input value={form.location_or_link} onChange={e => setForm(f => ({ ...f, location_or_link: e.target.value }))}
                      placeholder="Office address / room…"
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                  </div>
                ) : form.interview_type === "video" && scheduleMode === "direct" ? (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>Meeting Link</label>
                    <input value={form.location_or_link} onChange={e => setForm(f => ({ ...f, location_or_link: e.target.value }))}
                      placeholder="Leave blank — Google Meet link will be auto-generated"
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                  </div>
                ) : form.interview_type === "video" && scheduleMode === "propose" ? (
                  <div style={{ marginBottom: 12, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 12, color: "#15803d" }}>
                    🤖 Automatic Scheduling — the interviewer picks 3 slots, the candidate confirms one, and a Google Meet link is auto-generated.
                  </div>
                ) : null}

                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Interviewer</div>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 3 }}>Pick from employees (optional)</label>
                    <select value={form.interviewer_id}
                      onChange={e => {
                        const emp = (employees as any[]).find((x: any) => String(x.id) === e.target.value);
                        setForm(f => ({
                          ...f,
                          interviewer_id: e.target.value,
                          interviewer_name: emp ? emp.name : f.interviewer_name,
                          interviewer_email: emp ? (emp.email || f.interviewer_email) : f.interviewer_email,
                        }));
                      }}
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const, background: "white" }}>
                      <option value="">— or enter manually below —</option>
                      {(employees as any[]).map((emp: any) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 3 }}>Name *</label>
                      <input value={form.interviewer_name} onChange={e => setForm(f => ({ ...f, interviewer_name: e.target.value }))}
                        placeholder="John Smith"
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 3 }}>Email *</label>
                      <input type="email" value={form.interviewer_email} onChange={e => setForm(f => ({ ...f, interviewer_email: e.target.value }))}
                        placeholder="john@company.com"
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                    </div>
                  </div>
                </div>

                {scheduleMode === "direct" ? (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                      Additional Attendees <span style={{ fontWeight: 400, color: "#64748b" }}>(comma-separated emails)</span>
                    </label>
                    <input value={form.additional_attendees} onChange={e => setForm(f => ({ ...f, additional_attendees: e.target.value }))}
                      placeholder="e.g. cto@company.com, vp@company.com"
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, boxSizing: "border-box" as const }} />
                  </div>
                ) : (
                  <div style={{ marginBottom: 16, fontSize: 12, color: "#64748b" }}>
                    Additional attendees aren't supported yet for Automatic Scheduling — add them after the candidate confirms a slot, from the Interview Hub list.
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
                  <button onClick={() => setModalStep(1)}
                    style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 16px", cursor: "pointer", fontSize: 13, color: "#64748b" }}>
                    ← Back
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={closeModal}
                      style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "9px 16px", cursor: "pointer", fontSize: 13, color: "#64748b" }}>
                      Cancel
                    </button>
                    <button onClick={handleSend}
                      disabled={sending || (scheduleMode === "direct" ? !form.interview_date : (!form.interviewer_email || !form.interviewer_name))}
                      style={{ background: sending || (scheduleMode === "direct" ? !form.interview_date : (!form.interviewer_email || !form.interviewer_name)) ? "#94a3b8" : "#1e3a8a", color: "white", border: "none", padding: "9px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      {sending ? "Sending…" : scheduleMode === "direct" ? "Send Invites" : "Start Automatic Scheduling"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Row detail ── centered dialog on desktop, bottom sheet on mobile ── */}
      {detailRow && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
            display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center",
            padding: isMobile ? 0 : 16,
          }}
          onClick={() => setDetailRow(null)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Interview with ${detailRow.candidate_name}`}
            onClick={e => e.stopPropagation()}
            style={{
              background: "white", width: "100%", maxWidth: isMobile ? "none" : 560,
              borderRadius: isMobile ? "14px 14px 0 0" : 14,
              maxHeight: isMobile ? "88vh" : "90vh", overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}>
            {isMobile && <div style={{ width: 40, height: 4, background: "#e2e8f0", borderRadius: 2, margin: "10px auto 0" }} />}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "18px 20px 14px", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                <Avatar name={detailRow.candidate_name} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", overflowWrap: "anywhere" }}>{detailRow.candidate_name}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, overflowWrap: "anywhere" }}>
                    {detailRow.job_title} · with {detailRow.interviewer_name || "—"}
                  </div>
                </div>
              </div>
              <button onClick={() => setDetailRow(null)} aria-label="Close"
                style={{ display: "grid", placeItems: "center", minWidth: 32, minHeight: 32, background: "none", border: "none", cursor: "pointer", color: "#64748b", flexShrink: 0 }}>
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <StatusPill status={detailRow.status} />
                <TypeChip type={detailRow.interview_type} duration={detailRow.duration_minutes} />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Slot</div>
                {displaySlot(detailRow)
                  ? <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 600 }}>{displaySlot(detailRow)}</div>
                  : <div style={{ fontSize: 13, color: "#64748b" }}>Not scheduled</div>}
                {detailRow.timezone && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{detailRow.timezone}</div>}
              </div>

              {/* Scorecard + Reschedule are deliberately inert. `Interview` has no
                  numeric rating column and Hub rows are slot requests, not
                  interviews — see .claude/specs/interview-hub-ui-parity.md. */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, opacity: 0.65 }} aria-disabled="true">
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Scorecard</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} disabled
                      style={{ minWidth: 40, minHeight: 36, borderRadius: 6, border: "1px solid #e2e8f0", background: "white", color: "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "not-allowed" }}>{n}</button>
                  ))}
                </div>
                <textarea disabled placeholder="Feedback notes…"
                  style={{ width: "100%", minHeight: 64, border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", color: "#94a3b8", background: "white", boxSizing: "border-box", resize: "none", cursor: "not-allowed" }} />
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>Interview scorecards are coming soon.</div>
              </div>

              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, opacity: 0.65 }} aria-disabled="true">
                <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Reschedule</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>Proposing a new slot from the Hub is coming soon. Delete this interview and create a new one in the meantime.</div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "14px 20px 18px", borderTop: "1px solid #e2e8f0", flexWrap: "wrap" }}>
              {detailRow.candidate_id && (
                <button onClick={() => { const id = detailRow.candidate_id; setDetailRow(null); nav(`/candidates?highlight=${id}`); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", minHeight: isMobile ? 44 : 38, borderRadius: 6, border: "none", background: "#1e3a8a", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  View Profile <ArrowRight size={14} strokeWidth={2} />
                </button>
              )}
              <button onClick={() => setDetailRow(null)}
                style={{ padding: "10px 16px", minHeight: isMobile ? 44 : 38, borderRadius: 6, border: "1px solid #cbd5e1", background: "white", color: "#334155", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
